/* Value Stream Mapping Builder — flow-diagram-driven version with swimlanes & pain-points. */
import { useState } from "react";
import ToolWizard from "./ToolWizard";
import VsmFlowEditor, { collectPainPoints, aggregateBlocks } from "./VsmFlowEditor";
import { ArrowDown, ArrowUp, AlertTriangle } from "lucide-react";

function pceCalc(blocks) {
  const cycle = blocks.reduce((a, b) => a + b.cycle_time_min, 0);
  const changeover = blocks.reduce((a, b) => a + b.changeover_min, 0);
  const inventory = blocks.reduce((a, b) => a + b.inventory_units, 0);
  const leadTime = cycle + changeover + inventory * 5;
  const valueAdd = cycle;
  const pce = leadTime > 0 ? (valueAdd / leadTime) * 100 : 0;
  const avgDefect = blocks.length ? (blocks.reduce((a, b) => a + b.defect_pct, 0) / blocks.length) : 0;
  const avgUptime = blocks.length ? (blocks.reduce((a, b) => a + b.uptime_pct, 0) / blocks.length) : 0;
  return { cycle, changeover, inventory, leadTime, valueAdd, pce, avgDefect, avgUptime };
}

const SEV_COLOR = { H: "bg-red-100 text-red-700", M: "bg-amber-100 text-amber-700", L: "bg-blue-100 text-blue-700" };

function BlocksTable({ blocks }) {
  const [sortBy, setSortBy] = useState("cycle_time_min");
  const [sortDir, setSortDir] = useState("desc");
  const [groupBy, setGroupBy] = useState("none");

  const sorted = [...blocks].sort((a, b) => {
    const sa = typeof a[sortBy] === "string" ? a[sortBy] : Number(a[sortBy]);
    const sb = typeof b[sortBy] === "string" ? b[sortBy] : Number(b[sortBy]);
    return (sortDir === "asc" ? 1 : -1) * (sa < sb ? -1 : sa > sb ? 1 : 0);
  });

  const grouped = groupBy === "none" ? { "All blocks": sorted } : sorted.reduce((acc, b) => {
    const k = String(b[groupBy] || "—");
    acc[k] = acc[k] || []; acc[k].push(b); return acc;
  }, {});

  const Th = ({ k, children }) => (
    <th onClick={() => { if (sortBy === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(k); setSortDir("desc"); } }} className="text-left px-2 py-1.5 cursor-pointer hover:bg-sage-100 select-none">
      <span className="inline-flex items-center gap-1">{children}{sortBy === k && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}</span>
    </th>
  );

  return (
    <div data-testid="vsm-blocks-table">
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="text-sage-500">Group by:</span>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="border border-sage-200 rounded-md px-2 py-1 bg-white text-sage-700">
          <option value="none">None</option><option value="type">Block type</option><option value="h_lane_label">Phase (H-lane)</option><option value="v_lane_label">Performer (V-lane)</option>
        </select>
        <span className="text-sage-400 ml-auto">{blocks.length} blocks</span>
      </div>
      {Object.entries(grouped).map(([k, list]) => (
        <div key={k} className="mb-3">
          {groupBy !== "none" && <p className="text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">{k}</p>}
          <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
            <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr>
              <Th k="label">Block</Th><Th k="type">Type</Th><Th k="cycle_time_min">CT</Th><Th k="changeover_min">CO</Th><Th k="uptime_pct">Up%</Th><Th k="defect_pct">Def%</Th><Th k="operators">Ops</Th><Th k="painpoint_count">Pain</Th>
            </tr></thead>
            <tbody>
              {list.map(b => (
                <tr key={b.id} className="border-t border-sage-100">
                  <td className="px-2 py-1 font-semibold text-sage-900">{b.label}</td>
                  <td className="px-2 py-1 capitalize text-sage-700">{b.type}</td>
                  <td className="px-2 py-1 font-mono">{b.cycle_time_min}m</td>
                  <td className="px-2 py-1 font-mono">{b.changeover_min}m</td>
                  <td className="px-2 py-1 font-mono">{b.uptime_pct}%</td>
                  <td className="px-2 py-1 font-mono">{b.defect_pct}%</td>
                  <td className="px-2 py-1 font-mono">{b.operators}</td>
                  <td className="px-2 py-1">{b.painpoint_count > 0 ? <span className="bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 text-xs font-semibold">{b.painpoint_count}</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default function VsmWizard({ project, company, branding, onSave, onExportPDF }) {
  const steps = [
    {
      key: "scope", label: "Scope & Takt",
      render: ({ data, updateData }) => {
        const scope = data.scope || {};
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Product / Service family</label><input data-testid="vsm-product-family" value={scope.product_family || ""} onChange={e => updateData({ scope: { ...scope, product_family: e.target.value } })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Start boundary</label><input value={scope.start || ""} onChange={e => updateData({ scope: { ...scope, start: e.target.value } })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="Customer order received" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">End boundary</label><input value={scope.end || ""} onChange={e => updateData({ scope: { ...scope, end: e.target.value } })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="Product shipped" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Monthly demand (units)</label><input type="number" value={scope.volume || 0} onChange={e => updateData({ scope: { ...scope, volume: Number(e.target.value) } })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-semibold text-sage-600 mb-1">Takt time (minutes)</label><input type="number" value={scope.takt_minutes || 0} onChange={e => updateData({ scope: { ...scope, takt_minutes: Number(e.target.value) } })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          </div>
        );
      },
    },
    {
      key: "current_flow", label: "Current State Flow",
      render: ({ data, updateData }) => (
        <>
          <p className="text-xs text-sage-500 mb-3">Drag-drop blocks into swimlanes. Double-click a block to edit metrics & capture pain-points. Connect with arrows.</p>
          <VsmFlowEditor value={data.current_flow} onChange={(flow) => updateData({ current_flow: flow })} />
        </>
      ),
    },
    {
      key: "future_flow", label: "Future State Flow",
      render: ({ data, updateData }) => (
        <>
          <p className="text-xs text-sage-500 mb-3">Sketch the to-be flow with target metrics.</p>
          <VsmFlowEditor value={data.future_flow} onChange={(flow) => updateData({ future_flow: flow })} />
        </>
      ),
    },
    {
      key: "improvement", label: "Improvement Plan",
      render: ({ data, updateData }) => {
        const ideas = data.improvement?.ideas || [];
        const add = () => updateData({ improvement: { ideas: [...ideas, { title: "", owner: "", due: "", impact: "M" }] } });
        const upd = (i, p) => updateData({ improvement: { ideas: ideas.map((x, idx) => idx === i ? { ...x, ...p } : x) } });
        const rm = (i) => updateData({ improvement: { ideas: ideas.filter((_, idx) => idx !== i) } });
        return (
          <div className="space-y-2" data-testid="vsm-ideas">
            {ideas.map((it, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-sage-50 rounded-md p-3">
                <input placeholder="Idea" value={it.title} onChange={e => upd(i, { title: e.target.value })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                <input placeholder="Owner" value={it.owner} onChange={e => upd(i, { owner: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                <input type="date" value={it.due} onChange={e => upd(i, { due: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                <select value={it.impact} onChange={e => upd(i, { impact: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm bg-white"><option value="H">High impact</option><option value="M">Medium</option><option value="L">Low</option></select>
              </div>
            ))}
            <button onClick={add} className="border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full">+ Add idea</button>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const cur = aggregateBlocks(data.current_flow);
        const fut = aggregateBlocks(data.future_flow);
        const curStats = pceCalc(cur);
        const futStats = pceCalc(fut);
        const ltReduction = curStats.leadTime > 0 ? ((curStats.leadTime - futStats.leadTime) / curStats.leadTime) * 100 : 0;
        const allPains = collectPainPoints(data.current_flow);
        const top = allPains.slice(0, 5);
        return (
          <div className="space-y-5" data-testid="vsm-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ["Current Lead Time", `${curStats.leadTime.toFixed(1)} min`],
                ["Current PCE", `${curStats.pce.toFixed(1)}%`],
                ["Future Lead Time", `${futStats.leadTime.toFixed(1)} min`],
                ["Future PCE", `${futStats.pce.toFixed(1)}%`],
                ["Lead-time reduction", `${ltReduction.toFixed(1)}%`],
                ["Avg defect %", `${curStats.avgDefect.toFixed(1)}%`],
                ["Avg uptime %", `${curStats.avgUptime.toFixed(1)}%`],
                ["Pain-points captured", String(allPains.length)],
              ].map(([l, v]) => (
                <div key={l} className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-0.5">{l}</p><p className="font-heading text-base font-bold text-sage-900">{v}</p></div>
              ))}
            </div>

            {top.length > 0 && (
              <div>
                <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Top pain-points</h4>
                <div className="space-y-1.5" data-testid="diag-painpoints">
                  {top.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white border border-sage-100 rounded-md p-2 text-xs">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${SEV_COLOR[p.severity]}`}>{p.severity}</span>
                      <span className="font-semibold text-sage-900 flex-1 truncate">{p.title}</span>
                      <span className="text-sage-500">@ {p.node_label}</span>
                      <span className="bg-sage-50 border border-sage-200 rounded px-1.5 py-0.5 text-sage-600">{p.waste_tag}</span>
                      {p.cost_impact > 0 && <span className="text-sage-700 font-semibold">${p.cost_impact}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cur.length > 0 && (
              <div>
                <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Current state — sortable block view</h4>
                <BlocksTable blocks={cur} />
              </div>
            )}

            <button data-testid="export-vsm-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700">Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish & save" />;
}
